import React, { useState } from 'react';
import { type CancellationInfo } from '@gql/schema';
import { ModalDialog } from '@secretescapes/cauldron';
import {
  BadgeType,
  Badges,
  FormattedText,
  IconName,
  IconScale,
  Link,
  TypographyVariant,
  defaultHtmlMapping,
  useTheme,
} from '@secretescapes/falcon';
import { i18n } from '@secretescapes/i18n';
import { useTranslations } from '@utils/useTranslations';
import {
  BadgesWrapper,
  ModalContentWrapper,
  NonRefundableBadgeLink,
  RefundableBadgeLink,
  WrappedIcon,
} from './RefundabilityBadge.styles';

interface Props {
  isRefundableCheckInDate?: boolean;
  cancellationInfo: CancellationInfo;
  isFlightOptionSelected: boolean;
}

export const RefundabilityBadge = ({
  isRefundableCheckInDate,
  cancellationInfo,
  isFlightOptionSelected,
}: Props) => {
  const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
  const theme = useTheme();
  const { translations } = useTranslations();

  const getBadge = () => {
    if (!cancellationInfo?.isRefundable) {
      return (
        <Badges
          backgroundColor={theme.color.grey05}
          fontColor={theme.color.black}
          badgeType={BadgeType.INFORMATIONAL}
          icon={
            <WrappedIcon
              name={IconName.nonRefundable}
              fillColor={theme.color.black}
              size={IconScale.m}
            />
          }
          dataAttributes={{
            testid: 'non-refundable-badge',
          }}
        >
          <NonRefundableBadgeLink
            onClick={() => {
              setIsCancellationModalOpen(true);
            }}
            variant={TypographyVariant.body}
            component="a"
            componentProps={{ href: 'javascript:void(0)' }}
          >
            {i18n('non.refundable.sale.title', [], translations)}
          </NonRefundableBadgeLink>
        </Badges>
      );
    }

    if (isRefundableCheckInDate === false) {
      return (
        <Badges
          backgroundColor={theme.color.grey05}
          fontColor={theme.color.black}
          badgeType={BadgeType.INFORMATIONAL}
          icon={
            <WrappedIcon
              name={IconName.nonRefundable}
              fillColor={theme.color.black}
              size={IconScale.m}
            />
          }
          dataAttributes={{
            testid: 'non-refundable-dates-badge',
          }}
        >
          <NonRefundableBadgeLink
            onClick={() => {
              setIsCancellationModalOpen(true);
            }}
            variant={TypographyVariant.body}
            component="a"
            componentProps={{ href: 'javascript:void(0)' }}
          >
            {i18n('cancellation.non.refundable.dates.badge.title', [], translations)}
          </NonRefundableBadgeLink>
        </Badges>
      );
    }

    return (
      <Badges
        backgroundColor={theme.color.success05}
        fontColor={theme.color.success70}
        badgeType={BadgeType.INFORMATIONAL}
        icon={
          <WrappedIcon
            name={IconName.refundable}
            fillColor={theme.color.success70}
            size={IconScale.m}
          />
        }
        dataAttributes={{
          testid: 'refundable-badge',
        }}
      >
        <RefundableBadgeLink
          onClick={() => {
            setIsCancellationModalOpen(true);
          }}
          variant={TypographyVariant.body}
          component="a"
          componentProps={{ href: 'javascript:void(0)' }}
        >
          {i18n('cancellation.refundable.label', [], translations)}
        </RefundableBadgeLink>
      </Badges>
    );
  };

  return (
    <>
      <ModalDialog
        title={i18n('sale.booking.cancellation.policy.header', [], translations)}
        isOpen={isCancellationModalOpen}
        onClose={() => setIsCancellationModalOpen(false)}
      >
        <ModalContentWrapper>
          {cancellationInfo?.isRefundable &&
          isRefundableCheckInDate === false &&
          !!cancellationInfo.numberOfDays ? (
            <>
              <FormattedText
                html={i18n(
                  isFlightOptionSelected
                    ? 'cancellation.non.refundable.dates.with.flights'
                    : 'cancellation.days.before.policy.calendar.warning',
                  ['', cancellationInfo.numberOfDays],
                  translations
                )}
                htmlMapping={{
                  ...defaultHtmlMapping,
                  a: TypographyVariant.linkSecondary,
                }}
              />
              <FormattedText
                html={i18n(
                  isFlightOptionSelected
                    ? 'cancellation.non.refundable.dates.with.flights.warning'
                    : 'cancellation.non.refundable.dates.warning',
                  [],
                  translations
                )}
                htmlMapping={{
                  ...defaultHtmlMapping,
                  a: TypographyVariant.linkSecondary,
                }}
              />
              <Link href="/help-centre#faq-refundable-hotels">
                {i18n('cancellation.days.before.policy.find.out.more', [], translations)}
              </Link>
            </>
          ) : (
            <>
              <FormattedText
                html={`${isFlightOptionSelected ? cancellationInfo.descriptionWithFlights : cancellationInfo.description}`}
                htmlMapping={{
                  ...defaultHtmlMapping,
                  a: TypographyVariant.linkSecondary,
                }}
              />
              <FormattedText
                html={`${isFlightOptionSelected ? cancellationInfo.descriptionWarningWithFlights : cancellationInfo.descriptionWarning}`}
                htmlMapping={{
                  ...defaultHtmlMapping,
                  a: TypographyVariant.linkSecondary,
                }}
              />
            </>
          )}
        </ModalContentWrapper>
      </ModalDialog>
      <BadgesWrapper>{getBadge()}</BadgesWrapper>
    </>
  );
};
